import axios from 'axios';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api',
});

// Attach JWT to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('chamfb_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (loginId: string, password: string) =>
  api.post('/auth/login', { login: loginId, password });
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboard = () => api.get('/dashboard');
export const getDashboardMembre = () => api.get('/dashboard/membre');
export const getStatistics = () => api.get('/statistics');

// Projets
export const getProjets = () => api.get('/projets');
export const getProjet = (id: string) => api.get(`/projets/${id}`);
export const createProjet = (data: any) => api.post('/projets', data);
export const updateProjet = (id: string, data: any) => api.put(`/projets/${id}`, data);
export const deleteProjet = (id: string) => api.delete(`/projets/${id}`);

// Parcelles
export const getParcelles = (params?: any) => api.get('/parcelles', { params });
export const createParcelle = (data: any) => api.post('/parcelles', data);
export const updateParcelle = (id: string, data: any) => api.put(`/parcelles/${id}`, data);
export const deleteParcelle = (id: string) => api.delete(`/parcelles/${id}`);
export const searchMembresForParcelle = (q: string) => api.get('/parcelles/search-membres', { params: { q } });

// Membres
export const getMembres = (params?: any) => api.get('/membres', { params });
export const getMembresDirections = () => api.get('/membres/directions');
export const getMembre = (id: string) => api.get(`/membres/${id}`);
export const createMembre = (data: any) => api.post('/membres', data);
export const updateMembre = (id: string, data: any) => api.put(`/membres/${id}`, data);
export const deleteMembre = (id: string) => api.delete(`/membres/${id}`);
export const changePassword = (id: string, data: any) => api.put(`/membres/${id}/password`, data);
export const resetMemberPassword = (id: string) => api.post(`/membres/${id}/reset-password`);

// Versements
export const getVersements = (params?: any) => api.get('/versements', { params });
export const createVersement = (data: any) => api.post('/versements', data);
export const updateVersement = (id: string, data: any) => api.put(`/versements/${id}`, data);
export const deleteVersement = (id: string) => api.delete(`/versements/${id}`);

// Charges
export const getCharges = (params?: any) => api.get('/charges', { params });
export const createCharge = (data: any) => api.post('/charges', data);
export const updateCharge = (id: string, data: any) => api.put(`/charges/${id}`, data);
export const deleteCharge = (id: string) => api.delete(`/charges/${id}`);

// Documents
export const getDocuments = (params?: any) => api.get('/documents', { params });
export const createDocument = (data: any) => {
  if (data instanceof FormData) {
    return api.post('/documents', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return api.post('/documents', data);
};
export const updateDocument = (id: string, data: any) => api.put(`/documents/${id}`, data);
export const deleteDocument = (id: string) => api.delete(`/documents/${id}`);
export const downloadDocumentUrl = (id: string) => `${api.defaults.baseURL}/documents/download/${id}`;

// Articles
export const getArticles = () => api.get('/articles');
export const createArticle = (data: any) => {
  if (data instanceof FormData) {
    return api.post('/articles', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return api.post('/articles', data);
};
export const updateArticle = (id: string, data: any) => {
  if (data instanceof FormData) {
    return api.put(`/articles/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return api.put(`/articles/${id}`, data);
};
export const deleteArticle = (id: string) => api.delete(`/articles/${id}`);
export const articleImageUrl = (filename: string) => `${api.defaults.baseURL}/articles/image/${filename}`;

// Messages
export const getMessages = (params?: any) => api.get('/messages', { params });
export const createMessage = (data: any) => api.post('/messages', data);
export const deleteMessage = (id: string) => api.delete(`/messages/${id}`);

// Messagerie (Email/SMS)
export const getMessagerieRecipients = (params: any) => api.get('/messagerie/recipients', { params });
export const getMessagerieHistory = () => api.get('/messagerie/history');
export const sendEmail = (data: FormData) => api.post('/messagerie/send-email', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const sendSms = (data: any) => api.post('/messagerie/send-sms', data);
export const previewMessageApi = (data: any) => api.post('/messagerie/preview', data);

// Tirages
export const getTirages = () => api.get('/tirages');
export const getTirage = (id: string) => api.get(`/tirages/${id}`);
export const getAvailableParcelles = (projetId: string) => api.get('/tirages/available/parcelles', { params: { projet_id: projetId } });
export const getAvailableMembres = (projetId: string, directionId?: string) => api.get('/tirages/available/membres', { params: { projet_id: projetId, direction_id: directionId } });
export const getDirections = () => api.get('/tirages/available/directions');
export const executeTirage = (data: any) => api.post('/tirages/execute', data);
export const updateTirage = (id: string, data: any) => api.put(`/tirages/${id}`, data);
export const deleteTirage = (id: string) => api.delete(`/tirages/${id}`);

// Parametrage
export const getParametrage = (table: string) => api.get(`/parametrage/${table}`);
export const createParametrage = (table: string, data: any) => api.post(`/parametrage/${table}`, data);
export const updateParametrage = (table: string, id: string, data: any) => api.put(`/parametrage/${table}/${id}`, data);
export const deleteParametrage = (table: string, id: string) => api.delete(`/parametrage/${table}/${id}`);

// Chargements
export const getChargements = () => api.get('/chargements');
export const uploadChargement = (data: FormData) => api.post('/chargements/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteChargement = (id: string) => api.delete(`/chargements/${id}`);

// Cartographie
export const getCartoProjets = () => api.get('/cartographie/projets');
export const getCartoParcelles = (projetId: string) => api.get(`/cartographie/parcelles/${projetId}`);
export const uploadGpx = (projetId: string, data: FormData) => api.post(`/cartographie/upload-gpx/${projetId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const attribuerParcelle = (parcelleId: string, userId: string | null) => api.put(`/cartographie/attribuer/${parcelleId}`, { user_id: userId });
export const createParcelleCarto = (data: any) => api.post('/cartographie/create-parcelle', data);
export const searchMembresCarto = (q: string) => api.get('/cartographie/search-membres', { params: { q } });

// Admin Modules
export const getAvailableModules = () => api.get('/admin-modules/available');
export const getAdminDirectionUsers = () => api.get('/admin-modules/admins');
export const getAdminModules = (userId: string) => api.get(`/admin-modules/${userId}`);
export const updateAdminModules = (userId: string, modules: any[]) => api.put(`/admin-modules/${userId}`, { modules });
export const promoteToAdminDirection = (userId: string, modules: any[]) => api.post(`/admin-modules/promote/${userId}`, { modules });
export const demoteAdminDirection = (userId: string) => api.post(`/admin-modules/demote/${userId}`);

export default api;
